/**
 * Filters – Save/serialize logic
 */

import { showToast } from '../../main';
import { TOAST_FAILED_SAVE_FILTERS } from '../../utils/app-strings';
import { preferencesApi } from '../../api';
import type { FiltersJson, PreferencesUpdateData } from '../../api';
import { debounce, DISTANCE_SLIDER } from '../../utils';
import { store } from '../../state';
import type { FiltersState } from './types';

export const DEBOUNCE_KEY = 'filters-save';

/**
 * Build discovery filters payload.
 * sparse=true (default): only non-default values, for incremental saves.
 * sparse=false: all fields explicitly, for reset (server merges, so omitted fields persist).
 */
export function buildDiscoveryFilters(filters: FiltersState, sparse: boolean = true): Partial<FiltersJson> {
  const discovery: Partial<FiltersJson> = {};

  // Two-way matching toggle
  if (!sparse || filters.two_way_match) {
    discovery.two_way_match = filters.two_way_match;
  }

  // Intelligence substrate filters
  if (!sparse || !filters.show_human_users) {
    discovery.show_human_users = filters.show_human_users;
  }
  if (!sparse || filters.show_ai_users) {
    discovery.show_ai_users = filters.show_ai_users;
  }

  // Core range filters
  if (!sparse || filters.distance_max_km !== DISTANCE_SLIDER.UNLIMITED) {
    discovery.distance_max_km = filters.distance_max_km;
  }
  if (!sparse || filters.interested_in.length > 0) {
    discovery.interested_in = filters.interested_in;
  }
  if (!sparse || filters.relationship_intents.length > 0) {
    discovery.relationship_intents = filters.relationship_intents;
  }
  if (!sparse || filters.recently_active_hours !== null) {
    discovery.recently_active_hours = filters.recently_active_hours;
  }
  if (!sparse || filters.height_min_cm !== null) {
    discovery.height_min_cm = filters.height_min_cm;
  }
  if (!sparse || filters.height_max_cm !== null) {
    discovery.height_max_cm = filters.height_max_cm;
  }
  if (!sparse || filters.has_kids) {
    discovery.has_kids = filters.has_kids as FiltersJson['has_kids'];
  }
  if (!sparse || filters.kids_youngest_ages.length > 0) {
    discovery.kids_youngest_ages = filters.kids_youngest_ages;
  }
  if (!sparse || filters.kids_counts.length > 0) {
    discovery.kids_counts = filters.kids_counts;
  }
  if (!sparse || filters.kids_at_home) {
    discovery.kids_at_home = filters.kids_at_home as FiltersJson['kids_at_home'];
  }
  if (!sparse || filters.smoking.length > 0) {
    discovery.smoking = filters.smoking;
  }
  if (!sparse || filters.drinking.length > 0) {
    discovery.drinking = filters.drinking;
  }
  if (!sparse || filters.cannabis.length > 0) {
    discovery.cannabis = filters.cannabis;
  }
  if (!sparse || filters.preferred_structure) {
    discovery.preferred_structure = filters.preferred_structure as FiltersJson['preferred_structure'];
  }
  if (!sparse || filters.open_to_long_distance.length > 0) {
    discovery.open_to_long_distance = filters.open_to_long_distance;
  }
  if (!sparse || filters.education_levels.length > 0) {
    discovery.education_levels = filters.education_levels;
  }
  if (!sparse || filters.employment_statuses.length > 0) {
    discovery.employment_statuses = filters.employment_statuses;
  }
  if (!sparse || filters.languages.length > 0) {
    discovery.languages = filters.languages;
  }
  if (!sparse || filters.pets_ok_with.length > 0) {
    discovery.pets_ok_with = filters.pets_ok_with;
  }
  if (!sparse || filters.ethnicities.length > 0) {
    discovery.ethnicities = filters.ethnicities;
  }
  if (!sparse || filters.political_views.length > 0) {
    discovery.political_views = filters.political_views;
  }
  if (!sparse || filters.income_ranges.length > 0) {
    discovery.income_ranges = filters.income_ranges;
  }
  if (!sparse || filters.body_types.length > 0) {
    discovery.body_types = filters.body_types;
  }
  if (!sparse || filters.sexual_roles.length > 0) {
    discovery.sexual_roles = filters.sexual_roles;
  }
  if (!sparse || filters.sexual_dynamics.length > 0) {
    discovery.sexual_dynamics = filters.sexual_dynamics;
  }
  if (!sparse || filters.drugs.length > 0) {
    discovery.drugs = filters.drugs;
  }
  if (!sparse || filters.wants_more_kids.length > 0) {
    discovery.wants_more_kids = filters.wants_more_kids;
  }
  if (!sparse || filters.open_to_relocation.length > 0) {
    discovery.open_to_relocation = filters.open_to_relocation;
  }
  if (!sparse || filters.religions.length > 0) {
    discovery.religions = filters.religions;
  }
  if (!sparse || filters.sexual_orientations.length > 0) {
    discovery.sexual_orientations = filters.sexual_orientations;
  }
  if (!sparse || filters.inclusive_of_trans_nb) {
    discovery.inclusive_of_trans_nb = filters.inclusive_of_trans_nb;
  }
  if (!sparse || filters.open_to_enm_poly) {
    discovery.open_to_enm_poly = filters.open_to_enm_poly;
  }
  if (!sparse || filters.exercise_levels.length > 0) {
    discovery.exercise_levels = filters.exercise_levels;
  }
  if (!sparse || filters.zodiac_signs.length > 0) {
    discovery.zodiac_signs = filters.zodiac_signs;
  }

  // Compound intent entries
  if (!sparse || filters.intent_entries.length > 0) {
    discovery.intent_entries = filters.intent_entries;
  }

  // Activity filters
  if (!sparse || filters.activity_liked_me) discovery.activity_liked_me = filters.activity_liked_me;
  if (!sparse || filters.activity_i_liked) discovery.activity_i_liked = filters.activity_i_liked;
  if (!sparse || filters.activity_i_disliked) discovery.activity_i_disliked = filters.activity_i_disliked;

  // Travel location (always include so server can clear on reset)
  discovery.travel_city = filters.travel_city;
  discovery.travel_state = filters.travel_state;
  discovery.travel_country = filters.travel_country;
  discovery.travel_lat = filters.travel_lat;
  discovery.travel_lng = filters.travel_lng;

  return discovery;
}

export async function updatePreferencesAndSync(data: PreferencesUpdateData): Promise<void> {
  const response = await preferencesApi.update(data);
  const filtersJson = response.data.filters_json;
  store.updatePreferences({
    age_min: response.data.age_min,
    age_max: response.data.age_max,
    distance_max_km: response.data.distance_max_km,
    filters_json: filtersJson,
  });

  // Update travel location display if it exists
  updateTravelLocationDisplay(filtersJson);
}

/** Update the travel location feedback div after a save */
function updateTravelLocationDisplay(filtersJson: any): void {
  const display = document.getElementById('travel-location-display');
  if (!display) return;

  const hasTravelFields = filtersJson?.travel_city || filtersJson?.travel_state || filtersJson?.travel_country;

  if (hasTravelFields && filtersJson?.travel_lat != null && filtersJson?.travel_lng != null) {
    display.textContent = `${Number(filtersJson.travel_lat).toFixed(4)}, ${Number(filtersJson.travel_lng).toFixed(4)}`;
    display.classList.remove('text-error');
  } else if (hasTravelFields) {
    display.textContent = 'Location not found';
    display.classList.add('text-error');
  } else {
    display.classList.remove('text-error');
    const profile = store.getState().user?.profile;
    if (profile?.latitude != null && profile?.longitude != null) {
      display.textContent = `${Number(profile.latitude).toFixed(4)}, ${Number(profile.longitude).toFixed(4)}`;
    } else {
      display.textContent = 'Set your location in your profile';
    }
  }
}

export function showSaveIndicator(indicatorId: string): void {
  const indicator = document.getElementById(`indicator-${indicatorId}`);
  if (indicator) {
    indicator.classList.add('visible');
    setTimeout(() => indicator.classList.remove('visible'), 2000);
  }
}

export function saveFilters(filters: FiltersState, indicatorId: string): void {
  debounce(DEBOUNCE_KEY, async () => {
    try {
      const discoveryFilters = buildDiscoveryFilters(filters, false);

      await updatePreferencesAndSync({
        distance_max_km: filters.distance_max_km,
        filters_json: discoveryFilters,
      });

      showSaveIndicator(indicatorId);
    } catch (err: any) {
      console.error('Failed to save filters:', err);
      showToast(TOAST_FAILED_SAVE_FILTERS, 'error');
    }
  }, 500);
}
